import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
  Modal,
  DatePicker,
  Select,
  Spin,
  Grid,
  Tag,
  Alert
} from "antd";
import {
  UserOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  IdcardOutlined,
  CameraOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ScissorOutlined
} from "@ant-design/icons";
import { createCustomer, updateCustomer, getCustomerById, getCustomerEditState, uploadRawImage, verifyPan } from "../api";
import { usePermissions } from "../../../hooks/usePermissions";
import { ActionType, ModuleType } from "components";
import PhoneCaptureButton from "../../../components/PhoneCapture/PhoneCaptureButton";
import ImageCropModal from "../../../components/ImageEditor/ImageCropModal";
import DigilockerModal from "../components/DigilockerModal";
import dayjs from "dayjs";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const { canViewCreateModule, canEditModule, canViewField, canEditField, role } = usePermissions();
  const isAdmin = (role || '').toLowerCase() === 'admin';
  // Approval-gated editing: non-admins may only change the fields an approved
  // edit request unlocked (null = not loaded yet).
  const [editRequest, setEditRequest] = useState(null);
  const [editFieldLabels, setEditFieldLabels] = useState({});
  const module = ModuleType.CUSTOMER;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [confirmModal, setConfirmModal] = useState({ open: false, type: 'success', content: '' });

  // Photo upload state
  const fileRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoData, setPhotoData] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [croppingPhoto, setCroppingPhoto] = useState(null);

  // Watch all values
  const values = Form.useWatch([], form);

  // Validate form whenever values change
  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setIsSubmittable(true);
        },
        () => {
          setIsSubmittable(false);
        },
      );
  }, [values, form]);

  // KYC State
  const [digilockerModalVisible, setDigilockerModalVisible] = useState(false);
  const [verifiedKycData, setVerifiedKycData] = useState(null);
  const [digilockerDocuments, setDigilockerDocuments] = useState([]);
  // ID numbers filled by a verified KYC fetch — locked so they can't be altered.
  // Only what the provider actually returned is locked (e.g. Aadhaar OTP gives
  // no PAN; DigiLocker masks Aadhaar unless the full number is available).
  const [kycPrefilled, setKycPrefilled] = useState({ aadhaar: false, pan: false, source: '' });

  // PAN verification state
  const [panVerifying, setPanVerifying] = useState(false);
  const [panResult, setPanResult] = useState(null); // Sandbox pan_verification.response payload

  const isEdit = Boolean(id);
  const unlockedFields = editRequest?.status === 'UNLOCKED' ? editRequest.fields || [] : [];
  const isEditLocked = isEdit && !isAdmin && editRequest?.status !== 'UNLOCKED';
  const fieldLocked = (field) => isEdit && !isAdmin && !unlockedFields.includes(field);
  const canEditPhoto = canEditField(module, 'photo') && !fieldLocked('photo');
  const entryType = new URLSearchParams(location.search).get("type");
  const isDigilockerFlow = entryType === "digilocker";
  // Manual entry (no verified KYC pre-fill) must supply the full identity —
  // the backend rejects the create otherwise.
  const isManualCreate = !isEdit && !verifiedKycData;
  const manualRequired = (message) => (isManualCreate ? [{ required: true, message }] : []);

  useEffect(() => {
    if (isEdit) {
      loadCustomer();
    } else if (isDigilockerFlow) {
      setDigilockerModalVisible(true);
    }
  }, [id, isDigilockerFlow]);

  const loadCustomer = async () => {
    setLoading(true);
    try {
      const [response, editState] = await Promise.all([
        getCustomerById(id),
        getCustomerEditState(id).catch(() => null),
      ]);
      const customer = response.data;
      setEditRequest(editState?.data?.editRequest || { status: 'NONE' });
      setEditFieldLabels(Object.fromEntries((editState?.data?.fields || []).map((f) => [f.key, f.label])));

      form.setFieldsValue({
        ...customer,
        dateOfBirth: customer.dateOfBirth ? dayjs(customer.dateOfBirth) : null,
      });

      // Set photo preview
      setPhotoPreview(customer.photo || '');
    } catch (error) {
      messageApi.error("Failed to load customer details");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };

  const handleDigilockerSuccess = (res) => {
    setDigilockerModalVisible(false);
    const profile = res.profile || {};
    // Mark as verified via DigiLocker. Address/aadhaar come from the parsed
    // Aadhaar XML when available (Aadhaar number is only set if unmasked).
    setVerifiedKycData({ ...(res.kycData || {}), _provider: 'digilocker', eaadhaar: profile.eaadhaar });
    setDigilockerDocuments(res.kycDocuments || []);
    const dlAadhaar = /^\d{12}$/.test(profile.aadhaar || '') ? profile.aadhaar : '';
    const dlPan = /^[A-Z]{5}[0-9]{4}[A-Z]$/.test((profile.pan || '').toUpperCase()) ? profile.pan.toUpperCase() : '';
    setKycPrefilled({ aadhaar: !!dlAadhaar, pan: !!dlPan, source: 'DigiLocker' });

    form.setFieldsValue({
      name: profile.name,
      gender: profile.gender,
      dateOfBirth: profile.dateOfBirth ? dayjs(profile.dateOfBirth) : null,
      ...(profile.phone ? { phone: profile.phone } : {}),
      ...(profile.address ? { address: profile.address } : {}),
      ...(profile.city ? { city: profile.city } : {}),
      ...(profile.state ? { state: profile.state } : {}),
      ...(profile.pincode ? { pincode: profile.pincode } : {}),
      ...(dlAadhaar ? { aadhaar: dlAadhaar } : {}),
      ...(dlPan ? { pan: dlPan } : {}),
    });
    messageApi.success('Identity Verified. Form pre-filled from DigiLocker.');
  };

  const handleVerifyPan = async () => {
    const pan = (form.getFieldValue('pan') || '').toUpperCase();
    const name = form.getFieldValue('name');
    const dobVal = form.getFieldValue('dateOfBirth');

    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) {
      return messageApi.error('Please enter a valid PAN number before verifying');
    }

    setPanVerifying(true);
    setPanResult(null);
    try {
      const res = await verifyPan({
        pan,
        name,
        dob: dobVal ? dayjs(dobVal).format('DD/MM/YYYY') : undefined,
        ...(isEdit ? { customerId: id } : {}),
      });
      setPanResult(res.data);
      if (res.status === 'valid') {
        messageApi.success('PAN verified successfully');
      } else {
        messageApi.warning('PAN is not valid');
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'PAN verification failed';
      messageApi.error(msg);
      setPanResult(null);
    } finally {
      setPanVerifying(false);
    }
  };

  const handlePhotoClick = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setPhotoUploading(true);
    try {
      // Upload the image
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${isEdit ? id : 'new_customer'}_photo_${dateStr}.jpg`;
      const folderId = 'customer_photos';

      console.log('[CustomerForm] Uploading customer photo:', fileName);
      const uploadResponse = await uploadRawImage(file, { fileName, folderId });

      if (!uploadResponse.success || !uploadResponse.data) {
        throw new Error(uploadResponse.message || 'Upload failed');
      }

      const uploadedData = uploadResponse.data;
      setPhotoData(uploadedData);
      // Preview from the uploaded URL rather than a FileReader data: URL, so the
      // cropper has a real image source to work from.
      setPhotoPreview(uploadedData.thumbnailUrl || uploadedData.url);

      messageApi.success('Customer photo uploaded successfully');
    } catch (error) {
      console.error('Customer photo upload failed:', error);
      messageApi.error(error.message || 'Failed to upload customer photo');
    } finally {
      setPhotoUploading(false);
    }

    e.target.value = '';
  };

  const handlePhonePhoto = (incoming = []) => {
    const shot = incoming[incoming.length - 1];
    if (!shot) return;
    // Already uploaded to ImageKit by the capture endpoint — store the same
    // shape handlePhotoUpload produces so submit persists it identically.
    setPhotoData({
      fileId: shot.fileId,
      url: shot.url,
      thumbnailUrl: shot.thumbnailUrl,
      filePath: shot.filePath,
      name: shot.name,
    });
    setPhotoPreview(shot.thumbnailUrl || shot.url);
    messageApi.success('Photo received from phone');
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (isManualCreate && !photoData?.url) {
        setConfirmModal({ open: true, type: 'error', content: 'Profile photo is required for manual entry' });
        return;
      }
      const payload = {
        ...values,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null,
        ...(photoData?.url ? { photo: photoData.url } : {}),
      };

      // Inject Verified KYC Data on Creation
      if (verifiedKycData && !isEdit) {
        const isDigilocker = verifiedKycData._provider === 'digilocker';
        payload.kycStatus = 'verified';
        payload.kycProvider = isDigilocker ? 'digilocker' : 'sandbox';
        payload.kycData = verifiedKycData;
        payload.aadhaarLinked = isDigilocker ? !!verifiedKycData.eaadhaar : true;
        if (isDigilocker && digilockerDocuments.length) {
          payload.kycDocuments = digilockerDocuments;
        }
      }

      // Persist PAN verification result on creation (edit flow persists on verify).
      if (panResult && !isEdit) {
        payload.panVerified = panResult.status === 'valid';
        payload.panVerificationData = panResult;
      }

      try {
        let res;
        if (isEdit) {
          res = await updateCustomer(id, payload);
        } else {
          console.log('Creating customer with payload:', payload);
          res = await createCustomer(payload);
        }
        const successMsg = (res && res.data && (res.data.message || res.data.msg)) || (isEdit ? 'Customer updated successfully' : 'Customer created successfully');
        console.log('Customer create/update response:', res);

        try { messageApi.info(successMsg); } catch (e) { console.log('messageApi.info failed', e); }
        setConfirmModal({ open: true, type: 'success', content: successMsg });
      } catch (err) {
        throw err;
      }
    } catch (error) {
      const serverMessage = error && error.response && (error.response.data && (error.response.data.message || error.response.data.error))
        ? (error.response.data.message || error.response.data.error)
        : (error && error.message) || (isEdit ? "Failed to update customer" : "Failed to create customer");
      setConfirmModal({ open: true, type: 'error', content: serverMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (!canViewCreateModule(module) && !isEdit) {
    return (
      <div className="text-center mt-5">
        <Title level={4} type="danger">
          You do not have permission to create customers.
        </Title>
      </div>
    );
  }

  if (!canEditModule(module) && isEdit) {
    return (
      <div className="text-center mt-5">
        <Title level={4} type="danger">
          You do not have permission to edit customers.
        </Title>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }


  return (
    <>
      {contextHolder}
      <Modal
        open={confirmModal.open}
        title={confirmModal.type === 'success' ? 'Success' : 'Error'}
        onOk={() => {
          setConfirmModal((s) => ({ ...s, open: false }));
          if (confirmModal.type === 'success') navigate('/customers');
        }}
        onCancel={() => setConfirmModal((s) => ({ ...s, open: false }))}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {confirmModal.content}
      </Modal>

      <ImageCropModal
        open={Boolean(croppingPhoto)}
        image={croppingPhoto}
        folderPath="/customer_photos"
        defaultAspect={1}
        onCancel={() => setCroppingPhoto(null)}
        onCropped={(fileRef) => {
          setPhotoData(fileRef);
          setPhotoPreview(fileRef.thumbnailUrl || fileRef.url);
          setCroppingPhoto(null);
        }}
      />

      {/* DigiLocker Modal for Pre-Check */}
      <DigilockerModal
        visible={digilockerModalVisible}
        onClose={() => navigate("/customers")}
        mode="pre-check"
        onSuccess={handleDigilockerSuccess}
      />

      <main className="container-fluid px-2 px-md-4 py-3">

        <Row justify="center">
          <Col xs={24} lg={20} xl={16}>
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
                boxShadow: "0 2px 12px rgba(22,119,255,0.06)",
              }}
              bodyStyle={{ padding: screens.xs ? 16 : 24 }}
            >
              <Row justify="space-between" align="middle" className="mb-4">
                <Col xs={24} sm={12}>
                  <Space>
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={() => navigate("/customers")}
                      aria-label="Back to Customers"
                    >
                      Back
                    </Button>
                    <Title level={3} className="mb-0">
                      {isEdit ? "Edit Customer" : "Add Customer"}
                    </Title>
                  </Space>
                </Col>
              </Row>

              {isEdit && !isAdmin && editRequest && (
                isEditLocked ? (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-3"
                    message="This customer is locked"
                    description={editRequest.status === 'PENDING'
                      ? 'An edit request is awaiting approval. Fields unlock once it is approved.'
                      : 'Raise an edit request from the customer page, naming the fields to change. Once approved you can edit them here.'}
                    action={<Button size="small" onClick={() => navigate(`/customers/${id}`)}>Go to customer</Button>}
                  />
                ) : (
                  <Alert
                    type="success"
                    showIcon
                    className="mb-3"
                    message={`Approved for editing: ${unlockedFields.map((f) => editFieldLabels[f] || f).join(', ')}`}
                    description="Only these fields can be changed, and only in this one save. The customer locks again afterwards."
                  />
                )
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
                size={screens.xs ? "small" : "middle"}
              >
                <Row gutter={[24, 0]}>
                  <Col xs={24}>
                    <Card
                      title={
                        <Space>
                          <UserOutlined />
                          <span>Personal Information</span>
                        </Space>
                      }
                      size="small"
                      className="mb-3"
                    >
                      {/* Photo Upload Section */}
                      {canViewField(module, 'photo') && (
                        <Row gutter={[16, 0]} style={{ marginTop: 16 }}>
                          <Col xs={24}>
                            <Form.Item label="Profile Photo" required={isManualCreate}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <input
                                  ref={fileRef}
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={handlePhotoUpload}
                                  disabled={!canEditPhoto}
                                />
                                <div
                                  onClick={canEditPhoto && !photoUploading ? handlePhotoClick : undefined}
                                  role="button"
                                  tabIndex={0}
                                  style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    cursor: canEditPhoto && !photoUploading ? 'pointer' : 'default',
                                    border: '1px solid #e8e8e8',
                                    background: '#fafafa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 16,
                                    color: '#666',
                                    opacity: canEditPhoto ? 1 : 0.6,
                                  }}
                                >
                                  {photoUploading ? (
                                    <Spin />
                                  ) : photoPreview ? (
                                    <img
                                      src={photoPreview}
                                      alt="customer"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                  ) : (
                                    <div style={{ textAlign: 'center' }}>
                                      <CameraOutlined style={{ fontSize: 24, marginBottom: 4 }} />
                                      <div style={{ fontSize: 12 }}>Upload Photo</div>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                                    {canEditPhoto ? 'Click to upload a profile photo' : 'Profile photo upload not permitted'}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
                                    Supported formats: JPG, PNG, GIF (Max 10MB)
                                  </div>
                                  {canEditPhoto && (
                                    <PhoneCaptureButton
                                      targetKey="customer.photo"
                                      targetLabel="Customer Profile Photo"
                                      folderPath="/customer_photos"
                                      recordId={isEdit ? id : undefined}
                                      maxFiles={1}
                                      onFiles={handlePhonePhoto}
                                      disabled={photoUploading}
                                      buttonText="Capture from Phone (QR)"
                                      buttonProps={{ size: 'small' }}
                                    />
                                  )}
                                  {canEditPhoto && photoData?.url && (
                                    <Button
                                      size="small"
                                      icon={<ScissorOutlined />}
                                      onClick={() => setCroppingPhoto(photoData)}
                                      disabled={photoUploading}
                                      style={{ marginLeft: 8 }}
                                    >
                                      Crop
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                      <Row gutter={[16, 0]}>
                        {canViewField(module, 'customerId') && isEdit && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Customer ID"
                              name="customerId"
                            >
                              <Input
                                placeholder="Auto-generated"
                                prefix={<IdcardOutlined />}
                                aria-label="Customer ID"
                                disabled
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'name') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Full Name"
                              name="name"
                              rules={[
                                { required: true, message: "Please enter full name" },
                                { min: 2, message: "Name must be at least 2 characters" }
                              ]}
                            >
                              <Input
                                placeholder="Enter full name"
                                prefix={<UserOutlined />}
                                aria-label="Full Name"
                                disabled={!!verifiedKycData || !canEditField(module, 'name') || fieldLocked('name')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'phone') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Phone Number"
                              name="phone"
                              rules={[
                                { required: true, message: "Please enter phone number" },
                                {
                                  pattern: /^[6-9]\d{9}$/,
                                  message: "Please enter valid 10-digit phone number"
                                }
                              ]}
                            >
                              <Input
                                placeholder="Enter phone number"
                                prefix={<PhoneOutlined />}
                                maxLength={10}
                                aria-label="Phone Number"
                                disabled={!canEditField(module, 'phone') || fieldLocked('phone')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'alternatePhone') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Alternate Phone Number"
                              name="alternatePhone"
                              rules={[
                                {
                                  pattern: /^[6-9]\d{9}$/,
                                  message: "Please enter valid 10-digit phone number"
                                }
                              ]}
                            >
                              <Input
                                placeholder="Enter alternate phone number (optional)"
                                prefix={<PhoneOutlined />}
                                maxLength={10}
                                aria-label="Alternate Phone Number"
                                disabled={!canEditField(module, 'alternatePhone') || fieldLocked('alternatePhone')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'email') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Email Address"
                              name="email"
                              rules={[
                                { type: "email", message: "Please enter valid email" }
                              ]}
                            >
                              <Input
                                placeholder="Enter email address"
                                prefix={<MailOutlined />}
                                aria-label="Email Address"
                                disabled={!canEditField(module, 'email') || fieldLocked('email')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'dateOfBirth') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Date of Birth"
                              name="dateOfBirth"
                              rules={manualRequired("Please select date of birth")}
                            >
                              <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Select date of birth"
                                format="DD/MM/YYYY"
                                aria-label="Date of Birth"
                                disabled={!!verifiedKycData || !canEditField(module, 'dateOfBirth') || fieldLocked('dateOfBirth')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'gender') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Gender"
                              name="gender"
                              rules={manualRequired("Please select gender")}
                            >
                              <Select
                                placeholder="Select gender"
                                aria-label="Gender"
                                disabled={!!verifiedKycData || !canEditField(module, 'gender') || fieldLocked('gender')}
                              >
                                <Option value="Male">Male</Option>
                                <Option value="Female">Female</Option>
                                <Option value="Other">Other</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        )}
                      </Row>

                      
                    </Card>
                  </Col>

                  <Col xs={24}>
                    <Card
                      title={
                        <Space>
                          <HomeOutlined />
                          <span>Address Information</span>
                        </Space>
                      }
                      size="small"
                      className="mb-3"
                    >
                      <Row gutter={[16, 0]}>
                        {canViewField(module, 'address') && (
                          <Col xs={24}>
                            <Form.Item
                              label="Address"
                              name="address"
                              rules={manualRequired("Please enter address")}
                            >
                              <Input.TextArea
                                placeholder="Enter complete address"
                                rows={3}
                                aria-label="Address"
                                disabled={!canEditField(module, 'address') || fieldLocked('address')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'city') && (
                          <Col xs={24} md={8}>
                            <Form.Item
                              label="City"
                              name="city"
                            >
                              <Input
                                placeholder="Enter city"
                                aria-label="City"
                                disabled={!canEditField(module, 'city') || fieldLocked('city')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'state') && (
                          <Col xs={24} md={8}>
                            <Form.Item
                              label="State"
                              name="state"
                            >
                              <Input
                                placeholder="Enter state"
                                aria-label="State"
                                disabled={!canEditField(module, 'state') || fieldLocked('state')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'pincode') && (
                          <Col xs={24} md={8}>
                            <Form.Item
                              label="PIN Code"
                              name="pincode"
                              rules={[
                                {
                                  pattern: /^\d{6}$/,
                                  message: "Please enter valid 6-digit PIN code"
                                }
                              ]}
                            >
                              <Input
                                placeholder="Enter PIN code"
                                maxLength={6}
                                aria-label="PIN Code"
                                disabled={!canEditField(module, 'pincode') || fieldLocked('pincode')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    </Card>
                  </Col>

                  <Col xs={24}>
                    <Card
                      title={
                        <Space>
                          <IdcardOutlined />
                          <span>Identity Documents</span>
                        </Space>
                      }
                      size="small"
                      className="mb-3"
                    >
                      <Row gutter={[16, 0]}>
                        {canViewField(module, 'aadhaar') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Aadhaar Number"
                              name="aadhaar"
                              extra={kycPrefilled.aadhaar ? `Fetched & verified via ${kycPrefilled.source}` : undefined}
                              rules={[
                                ...manualRequired("Please enter Aadhaar number"),
                                {
                                  pattern: /^\d{12}$/,
                                  message: "Please enter valid 12-digit Aadhaar number"
                                }
                              ]}
                            >
                              <Input
                                placeholder="Enter Aadhaar number"
                                maxLength={12}
                                aria-label="Aadhaar Number"
                                disabled={kycPrefilled.aadhaar || !canEditField(module, 'aadhaar') || fieldLocked('aadhaar')}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {canViewField(module, 'pan') && (
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="PAN Number"
                              name="pan"
                              extra={kycPrefilled.pan ? `Fetched & verified via ${kycPrefilled.source}` : undefined}
                              normalize={(value) => (value ? value.toUpperCase() : value)}
                              rules={[
                                {
                                  pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                                  message: "Please enter valid PAN number"
                                }
                              ]}
                            >
                              <Input
                                placeholder="Enter PAN number"
                                maxLength={10}
                                style={{ textTransform: 'uppercase' }}
                                aria-label="PAN Number"
                                disabled={kycPrefilled.pan || !canEditField(module, 'pan') || fieldLocked('pan')}
                                onChange={(e) => { setPanResult(null); form.setFieldsValue({ pan: e.target.value ? e.target.value.toUpperCase() : e.target.value }); }}
                                addonAfter={
                                  canEditField(module, 'pan') ? (
                                    <Button
                                      type="link"
                                      size="small"
                                      icon={<SafetyCertificateOutlined />}
                                      loading={panVerifying}
                                      onClick={handleVerifyPan}
                                      style={{ padding: 0, height: 'auto' }}
                                      aria-label="Verify PAN"
                                    >
                                      Verify
                                    </Button>
                                  ) : null
                                }
                              />
                            </Form.Item>
                            {panResult && (
                              <div style={{ marginTop: -12, marginBottom: 12 }}>
                                <Space size={[4, 4]} wrap>
                                  {panResult.status === 'valid' ? (
                                    <Tag icon={<CheckCircleOutlined />} color="success">Valid PAN</Tag>
                                  ) : (
                                    <Tag icon={<CloseCircleOutlined />} color="error">Invalid PAN</Tag>
                                  )}
                                  {typeof panResult.name_as_per_pan_match === 'boolean' && (
                                    <Tag color={panResult.name_as_per_pan_match ? 'success' : 'warning'}>
                                      Name {panResult.name_as_per_pan_match ? 'match' : 'mismatch'}
                                    </Tag>
                                  )}
                                  {typeof panResult.date_of_birth_match === 'boolean' && (
                                    <Tag color={panResult.date_of_birth_match ? 'success' : 'warning'}>
                                      DOB {panResult.date_of_birth_match ? 'match' : 'mismatch'}
                                    </Tag>
                                  )}
                                  {panResult.aadhaar_seeding_status && (
                                    <Tag color={panResult.aadhaar_seeding_status === 'y' ? 'success' : 'default'}>
                                      Aadhaar linked: {panResult.aadhaar_seeding_status === 'y' ? 'Yes' : 'No'}
                                    </Tag>
                                  )}
                                </Space>
                                {panResult.remarks && (
                                  <Alert
                                    type="info"
                                    message={panResult.remarks}
                                    showIcon
                                    style={{ marginTop: 8 }}
                                  />
                                )}
                              </div>
                            )}
                          </Col>
                        )}
                      </Row>
                    </Card>
                  </Col>

                  <Col xs={24}>
                    <Row justify="end">
                      <Col>
                        <Space>
                          <Button
                            onClick={() => navigate("/customers")}
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={submitting}
                            icon={<SaveOutlined />}
                            disabled={!isSubmittable || isEditLocked}
                            aria-label={isEdit ? "Update Customer" : "Create Customer"}
                          >
                            {isEdit ? "Update Customer" : "Create Customer"}
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
        </Row>
      </main>
    </>
  );
};

export default CustomerForm;
